/** Muted hero autoplay with a keyboard-operable video surface and reduced-motion poster. */
(() => {
  const mobileLayout = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const cleanupByVideo = new WeakMap();

  const init = (root) => {
    root.querySelectorAll('[data-br-home-hero-video]').forEach((video) => {
      if (video.dataset.brHeroReady) return;

      const frame = video.closest('[data-br-home-hero-frame]');
      const source = video.querySelector('source[data-src]');
      if (!frame || !source) return;
      video.dataset.brHeroReady = 'true';

      let sourceAttached = false;
      let userPaused = false;
      let manualRequested = false;
      let disposed = false;
      let observer;
      const listeners = [];
      const on = (target, name, callback, options) => {
        target.addEventListener(name, callback, options);
        listeners.push(() => target.removeEventListener(name, callback));
      };

      const syncControl = () => {
        frame.setAttribute(
          'aria-label',
          video.paused ? frame.dataset.labelPlay : frame.dataset.labelPause
        );
      };

      const attachSource = () => {
        if (sourceAttached) return;
        sourceAttached = true;
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        video.preload = 'auto';
        video.load();
      };

      const attemptPlay = (manual = false) => {
        if (disposed || (!manual && (userPaused || reducedMotion.matches || document.hidden)))
          return;
        video.defaultMuted = true;
        video.muted = true;
        attachSource();
        const attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.catch((error) => {
            if (disposed) return;
            manualRequested = false;
            video.dataset.brHeroPlaybackError = error.name;
            frame.removeAttribute('data-br-hero-started');
            syncControl();
          });
        }
      };

      // Move the one video, rather than visually reordering its keyboard control.
      // Keep the original desktop/tablet parent so resizing restores the layout.
      const mediaPanel = frame.closest('.br-hero-home-c__right');
      const master = mediaPanel?.parentElement;
      const copyPanel = master?.querySelector('.br-hero-home-c__left');
      const detail = copyPanel?.querySelector('.br-hero-home-c__bottom-tile');
      if (mediaPanel && master && copyPanel && detail) {
        const syncLayout = () => {
          const parent = mobileLayout.matches ? copyPanel : master;
          if (mediaPanel.parentElement === parent) return;
          const wasPlaying = !video.paused;
          const focused = mediaPanel.contains(document.activeElement)
            ? document.activeElement
            : null;
          if (mobileLayout.matches) copyPanel.insertBefore(mediaPanel, detail);
          else master.append(mediaPanel);
          focused?.focus({ preventScroll: true });
          // Reparenting can pause playback. Resume only an already-playing video;
          // under reduced motion that playback was explicitly requested.
          if (wasPlaying) attemptPlay(reducedMotion.matches);
        };
        syncLayout();
        on(mobileLayout, 'change', syncLayout);
      }

      on(video, 'playing', () => {
        if (userPaused) {
          video.pause();
          return;
        }
        manualRequested = false;
        delete video.dataset.brHeroPlaybackError;
        frame.setAttribute('data-br-hero-started', '');
        syncControl();
      });
      on(video, 'pause', () => {
        // An explicit pause freezes the current frame; other pauses show the poster.
        if (!userPaused) frame.removeAttribute('data-br-hero-started');
        syncControl();
      });
      on(
        video,
        'canplay',
        () => {
          if (manualRequested) attemptPlay(true);
          else attemptPlay();
        },
        { once: true }
      );

      const togglePlayback = () => {
        if (video.paused) {
          userPaused = false;
          manualRequested = true;
          attemptPlay(true);
        } else {
          userPaused = true;
          manualRequested = false;
          video.pause();
        }
        syncControl();
      };
      on(frame, 'click', togglePlayback);
      on(frame, 'keydown', (event) => {
        if (event.repeat || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        togglePlayback();
      });

      on(reducedMotion, 'change', () => {
        if (reducedMotion.matches) {
          manualRequested = false;
          video.pause();
          frame.removeAttribute('data-br-hero-started');
        } else {
          attemptPlay();
        }
      });
      on(document, 'visibilitychange', () => {
        if (document.hidden) video.pause();
        else attemptPlay();
      });
      on(window, 'pageshow', () => attemptPlay());
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) attemptPlay();
        });
        observer.observe(video);
      }

      frame.setAttribute('role', 'button');
      frame.setAttribute('data-br-hero-interactive', '');
      frame.tabIndex = 0;

      cleanupByVideo.set(video, () => {
        disposed = true;
        listeners.forEach((remove) => remove());
        observer?.disconnect();
        video.pause();
        frame.removeAttribute('role');
        frame.removeAttribute('aria-label');
        frame.removeAttribute('data-br-hero-interactive');
        frame.removeAttribute('tabindex');
        delete video.dataset.brHeroReady;
        cleanupByVideo.delete(video);
      });

      syncControl();
      attemptPlay();
    });
  };

  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-br-home-hero-video]').forEach((video) => {
      cleanupByVideo.get(video)?.();
    });
  });
})();
